import React, { useState, useEffect, useCallback } from 'react';
import { apiKeyAPI } from '../../../../services';
import { adminHasPermission, getCurrentAdmin, isAdminAuth } from '../../../../services/auth/authManager';
import { useNotification } from '../../../../services/notificationService';
import { handlePermissionError } from '../../../../services/utils/permissionErrorHandler';
import {
  getConfigEndpoint,
  setConfigEndpoint,
  getFirstActiveEndpoint
} from '../../../../services/endpoints/configEndpointManager';
import { endpointAPI } from '../../../../services';
import './ApiKeyManager.css';

const ApiKeyManager = () => {
  const { success, error: notifyError } = useNotification();
  const [apiKeys, setApiKeys] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0 });
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [currentAdmin, setCurrentAdmin] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);

  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    isActive: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    pages: 1
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientIp: '',
    expiresAt: '',
    rateLimitOverride: {
      trades: '',
      stats: ''
    },
    permissions: {
      canAccessTrades: true,
      canAccessStats: true
    }
  });

  useEffect(() => {
    const admin = getCurrentAdmin();
    setCurrentAdmin(admin);

    if (!isAdminAuth()) {
      setPageError('Please log in to access this page');
      return;
    }

    initializeAndLoad();
  }, []);

  const initializeConfigEndpoint = useCallback(async () => {
    const existingEndpoint = getConfigEndpoint();
    if (existingEndpoint && existingEndpoint !== '') {
      return;
    }

    const storedEndpoint = getFirstActiveEndpoint();
    if (storedEndpoint && storedEndpoint !== '') {
      setConfigEndpoint(storedEndpoint);
      return;
    }

    try {
      const response = await endpointAPI.getAll();
      if (response && response.data) {
        const endpointData = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        if (Array.isArray(endpointData) && endpointData.length > 0) {
          const formattedEndpoints = endpointData.map(endpoint => ({
            id: endpoint._id,
            name: endpoint.name || 'Unnamed Endpoint',
            baseUrl: endpoint.baseUrl || endpoint.url || '',
            url: endpoint.baseUrl || endpoint.url || '',
            status: endpoint.isActive ? 'active' : 'inactive',
            createdAt: endpoint.createdAt
          }));

          const firstActiveEndpoint = formattedEndpoints.find(e => e.status === 'active');
          const endpointToSet = firstActiveEndpoint?.baseUrl || formattedEndpoints[0]?.baseUrl || '';

          if (endpointToSet) {
            setConfigEndpoint(endpointToSet);
          }
        }
      }
    } catch (err) {
      console.error('[ApiKeyManager] Failed to fetch endpoints:', err);
    }
  }, []);

  const initializeAndLoad = async () => {
    await initializeConfigEndpoint();
    loadApiKeys();
    loadStats();
  };

  const loadApiKeys = useCallback(async (retryCount = 0) => {
    setLoading(true);
    setPageError(null);

    try {
      const params = { ...filters };
      if (!params.isActive) delete params.isActive;

      const result = await apiKeyAPI.getAll(params);
      setApiKeys(result.data || []);
      if (result.pagination) {
        setPagination(result.pagination);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setPageError('API Keys are not available in Web2 mode. This feature requires Web3 mode.');
        setApiKeys([]);
        setLoading(false);
        return;
      }

      if ((err.response?.status === 404 || err.code === 'ERR_NETWORK') && retryCount < 2) {
        await initializeConfigEndpoint();
        await new Promise(resolve => setTimeout(resolve, 500));
        return loadApiKeys(retryCount + 1);
      }

      const isPermError = handlePermissionError(err);
      if (!isPermError) {
        setPageError(err.response?.data?.message || 'Failed to load API keys');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, initializeConfigEndpoint]);

  const loadStats = async () => {
    try {
      const result = await apiKeyAPI.getStats();
      setStats(result.data || { total: 0, active: 0, expired: 0 });
    } catch (err) {
      if (err.response?.status === 403) {
        console.log('API Keys not available in Web2 mode');
        return;
      }
      console.error('Failed to load API key stats:', err);
    }
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        clientIp: formData.clientIp || undefined,
        expiresAt: formData.expiresAt || undefined,
        rateLimitOverride: {},
        permissions: formData.permissions
      };

      if (formData.rateLimitOverride.trades) {
        payload.rateLimitOverride.trades = parseInt(formData.rateLimitOverride.trades);
      }
      if (formData.rateLimitOverride.stats) {
        payload.rateLimitOverride.stats = parseInt(formData.rateLimitOverride.stats);
      }

      const result = await apiKeyAPI.create(payload);
      setNewlyCreatedKey(result.data);
      success('API key created successfully');
      setShowCreateModal(false);
      resetForm();
      loadApiKeys();
      loadStats();
    } catch (err) {
      const isPermError = handlePermissionError(err);
      if (!isPermError) {
        notifyError(err.response?.data?.message || 'Failed to create API key');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateApiKey = async (e) => {
    e.preventDefault();

    if (!selectedKey) return;

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        clientIp: formData.clientIp || undefined,
        expiresAt: formData.expiresAt || undefined,
        rateLimitOverride: {},
        permissions: formData.permissions
      };

      if (formData.rateLimitOverride.trades) {
        payload.rateLimitOverride.trades = parseInt(formData.rateLimitOverride.trades);
      }
      if (formData.rateLimitOverride.stats) {
        payload.rateLimitOverride.stats = parseInt(formData.rateLimitOverride.stats);
      }

      await apiKeyAPI.update(selectedKey.id, payload);
      success('API key updated successfully');
      setShowEditModal(false);
      resetForm();
      setSelectedKey(null);
      loadApiKeys();
    } catch (err) {
      const isPermError = handlePermissionError(err);
      if (!isPermError) {
        notifyError(err.response?.data?.message || 'Failed to update API key');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRotateApiKey = async (id) => {
    if (!window.confirm('This will invalidate the current key and generate a new one. Continue?')) {
      return;
    }

    setLoading(true);

    try {
      const result = await apiKeyAPI.rotate(id);
      setNewlyCreatedKey(result.data);
      success('API key rotated successfully');
      loadApiKeys();
      loadStats();
    } catch (err) {
      const isPermError = handlePermissionError(err);
      if (!isPermError) {
        notifyError(err.response?.data?.message || 'Failed to rotate API key');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeApiKey = async (id) => {
    if (!window.confirm('This will disable the API key. You can enable it again by editing. Continue?')) {
      return;
    }

    setLoading(true);

    try {
      await apiKeyAPI.revoke(id);
      success('API key revoked successfully');
      loadApiKeys();
      loadStats();
    } catch (err) {
      const isPermError = handlePermissionError(err);
      if (!isPermError) {
        notifyError(err.response?.data?.message || 'Failed to revoke API key');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApiKey = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this API key? This action cannot be undone.')) {
      return;
    }

    setLoading(true);

    try {
      await apiKeyAPI.delete(id);
      success('API key deleted successfully');
      loadApiKeys();
      loadStats();
    } catch (err) {
      const isPermError = handlePermissionError(err);
      if (!isPermError) {
        notifyError(err.response?.data?.message || 'Failed to delete API key');
      }
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (key) => {
    setSelectedKey(key);
    setFormData({
      name: key.name,
      description: key.description || '',
      clientIp: key.clientIp || '',
      expiresAt: key.expiresAt ? key.expiresAt.split('T')[0] : '',
      rateLimitOverride: {
        trades: key.rateLimitOverride?.trades || '',
        stats: key.rateLimitOverride?.stats || ''
      },
      permissions: {
        canAccessTrades: key.permissions?.canAccessTrades ?? true,
        canAccessStats: key.permissions?.canAccessStats ?? true
      }
    });
    setShowEditModal(true);
  };

  const openViewModal = (key) => {
    setSelectedKey(key);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      clientIp: '',
      expiresAt: '',
      rateLimitOverride: {
        trades: '',
        stats: ''
      },
      permissions: {
        canAccessTrades: true,
        canAccessStats: true
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatKeyDisplay = (keyPrefix) => {
    return keyPrefix ? `${keyPrefix}...` : 'N/A';
  };

  if (pageError && !apiKeys.length) {
    return (
      <div className="apikey-management">
        <div className="apikey-empty">
          <div className="apikey-empty-icon"><i className="fas fa-exclamation-triangle"></i></div>
          <h3 className="apikey-empty-title">Access Denied</h3>
          <p className="apikey-empty-text">{pageError}</p>
          <button
            className="btn btn-secondary"
            onClick={() => window.location.reload()}
          >
            <i className="fas fa-sync"></i> Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="apikey-management">
      {newlyCreatedKey && (
        <div className="modal-overlay" onClick={() => setNewlyCreatedKey(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-key"></i> API Key Created</h2>
              <button className="close-btn" onClick={() => setNewlyCreatedKey(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <div className="warning-banner">
                <i className="fas fa-exclamation-triangle"></i>
                <span>Store this key securely. It will not be shown again.</span>
              </div>
              <div className="key-display">
                <label>Your New API Key</label>
                <div className="key-value">{newlyCreatedKey.key}</div>
              </div>
              <div className="key-info">
                <div className="key-info-row">
                  <span className="key-info-label">Key Prefix:</span>
                  <span className="key-info-value">{newlyCreatedKey.keyPrefix}...</span>
                </div>
                <div className="key-info-row">
                  <span className="key-info-label">Name:</span>
                  <span className="key-info-value">{newlyCreatedKey.name}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setNewlyCreatedKey(null)}>
                I have stored the key securely
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-header">
        <div className="header-content">
          <div className="header-title">
            <div className="header-icon"><i className="fas fa-key"></i></div>
            <div>
              <h1>Third Party API Key Management</h1>
              <p>Create and manage API keys for third-party clients</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => { loadApiKeys(); loadStats(); }}>
              <i className="fas fa-sync"></i> Refresh
            </button>
            {adminHasPermission('system.super') && (
              <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
                <i className="fas fa-plus"></i> Create API Key
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-key"></i></div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Keys</div>
          </div>
        </div>
        <div className="stat-card active">
          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
          <div className="stat-content">
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        <div className="stat-card expired">
          <div className="stat-icon"><i className="fas fa-clock"></i></div>
          <div className="stat-content">
            <div className="stat-value">{stats.expired}</div>
            <div className="stat-label">Expired</div>
          </div>
        </div>
      </div>

      <div className="apikey-filters">
        <select
          className="apikey-filter-select"
          value={filters.isActive}
          onChange={(e) => setFilters({ ...filters, isActive: e.target.value, page: 1 })}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {loading && !apiKeys.length ? (
        <div className="apikey-loading">
          <div className="apikey-loading-spinner"></div>
          <p>Loading API keys...</p>
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="apikey-empty">
          <div className="apikey-empty-icon"><i className="fas fa-key"></i></div>
          <h3 className="apikey-empty-title">No API Keys Found</h3>
          <p className="apikey-empty-text">
            {filters.isActive !== '' || filters.page !== 1
              ? 'No API keys match your current filters.'
              : 'There are no API keys yet. Create one to get started.'}
          </p>
        </div>
      ) : (
        <>
          <div className="apikey-table-container">
            <table className="apikey-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key Prefix</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Last Used</th>
                  <th>Usage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.id} className={!key.isActive ? 'inactive' : ''}>
                    <td>
                      <div className="key-name-cell">
                        <span className="key-name">{key.name}</span>
                        {key.description && (
                          <span className="key-description">{key.description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <code className="key-prefix">{formatKeyDisplay(key.keyPrefix)}</code>
                    </td>
                    <td>
                      <span className={`status-badge ${key.isActive ? 'active' : 'inactive'}`}>
                        <i className={`fas ${key.isActive ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                        {key.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{formatDate(key.expiresAt)}</td>
                    <td>{formatDate(key.lastUsedAt)}</td>
                    <td>{key.usageCount || 0}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn"
                          onClick={() => openViewModal(key)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {adminHasPermission('system.super') && (
                          <>
                            <button
                              className="action-btn"
                              onClick={() => openEditModal(key)}
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="action-btn"
                              onClick={() => handleRotateApiKey(key.id)}
                              title="Rotate Key"
                            >
                              <i className="fas fa-sync"></i>
                            </button>
                            <button
                              className={`action-btn ${key.isActive ? 'warning' : ''}`}
                              onClick={() => handleRevokeApiKey(key.id)}
                              title={key.isActive ? 'Revoke' : 'Enable'}
                            >
                              <i className={`fas ${key.isActive ? 'fa-ban' : 'fa-check'}`}></i>
                            </button>
                            <button
                              className="action-btn danger"
                              onClick={() => handleDeleteApiKey(key.id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={pagination.page <= 1}
                onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
              </span>
              <button
                className="pagination-btn"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-plus"></i> Create New API Key</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleCreateApiKey}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="e.g., Partner A"
                    />
                  </div>
                  <div className="form-group">
                    <label>Client IP</label>
                    <input
                      type="text"
                      value={formData.clientIp}
                      onChange={(e) => setFormData({ ...formData, clientIp: e.target.value })}
                      placeholder="e.g., 203.0.113.50 (optional)"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the purpose of this API key"
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Expires At</label>
                  <input
                    type="date"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-section">
                  <h4>Rate Limit Overrides</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Trades Limit (per min)</label>
                      <input
                        type="number"
                        value={formData.rateLimitOverride.trades}
                        onChange={(e) => setFormData({
                          ...formData,
                          rateLimitOverride: { ...formData.rateLimitOverride, trades: e.target.value }
                        })}
                        placeholder="Default: 60"
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Stats Limit (per min)</label>
                      <input
                        type="number"
                        value={formData.rateLimitOverride.stats}
                        onChange={(e) => setFormData({
                          ...formData,
                          rateLimitOverride: { ...formData.rateLimitOverride, stats: e.target.value }
                        })}
                        placeholder="Default: 120"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Permissions</h4>
                  <div className="permission-checkboxes">
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.permissions.canAccessTrades}
                        onChange={(e) => setFormData({
                          ...formData,
                          permissions: { ...formData.permissions, canAccessTrades: e.target.checked }
                        })}
                      />
                      <span>Can Access Trades</span>
                    </label>
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.permissions.canAccessStats}
                        onChange={(e) => setFormData({
                          ...formData,
                          permissions: { ...formData.permissions, canAccessStats: e.target.checked }
                        })}
                      />
                      <span>Can Access Stats</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create API Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedKey && (
        <div className="modal-overlay" onClick={() => { setShowEditModal(false); resetForm(); setSelectedKey(null); }}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-edit"></i> Edit API Key</h2>
              <button className="close-btn" onClick={() => { setShowEditModal(false); resetForm(); setSelectedKey(null); }}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleUpdateApiKey}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Client IP</label>
                    <input
                      type="text"
                      value={formData.clientIp}
                      onChange={(e) => setFormData({ ...formData, clientIp: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Expires At</label>
                  <input
                    type="date"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  />
                </div>

                <div className="form-section">
                  <h4>Rate Limit Overrides</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Trades Limit (per min)</label>
                      <input
                        type="number"
                        value={formData.rateLimitOverride.trades}
                        onChange={(e) => setFormData({
                          ...formData,
                          rateLimitOverride: { ...formData.rateLimitOverride, trades: e.target.value }
                        })}
                        placeholder="Default: 60"
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Stats Limit (per min)</label>
                      <input
                        type="number"
                        value={formData.rateLimitOverride.stats}
                        onChange={(e) => setFormData({
                          ...formData,
                          rateLimitOverride: { ...formData.rateLimitOverride, stats: e.target.value }
                        })}
                        placeholder="Default: 120"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Permissions</h4>
                  <div className="permission-checkboxes">
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.permissions.canAccessTrades}
                        onChange={(e) => setFormData({
                          ...formData,
                          permissions: { ...formData.permissions, canAccessTrades: e.target.checked }
                        })}
                      />
                      <span>Can Access Trades</span>
                    </label>
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.permissions.canAccessStats}
                        onChange={(e) => setFormData({
                          ...formData,
                          permissions: { ...formData.permissions, canAccessStats: e.target.checked }
                        })}
                      />
                      <span>Can Access Stats</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); resetForm(); setSelectedKey(null); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && selectedKey && (
        <div className="modal-overlay" onClick={() => { setShowViewModal(false); setSelectedKey(null); }}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-key"></i> {selectedKey.name}</h2>
              <button className="close-btn" onClick={() => { setShowViewModal(false); setSelectedKey(null); }}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <div className="view-details-section">
                <h4 className="view-details-title"><i className="fas fa-info-circle"></i> Basic Information</h4>
                <div className="view-details-grid">
                  <div className="view-detail-item">
                    <span className="view-detail-label">Key Prefix</span>
                    <span className="view-detail-value"><code>{selectedKey.keyPrefix}...</code></span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Status</span>
                    <span className={`status-badge ${selectedKey.isActive ? 'active' : 'inactive'}`}>
                      <i className={`fas ${selectedKey.isActive ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                      {selectedKey.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Created</span>
                    <span className="view-detail-value">{formatDate(selectedKey.createdAt)}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Expires</span>
                    <span className="view-detail-value">{formatDate(selectedKey.expiresAt)}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Client IP</span>
                    <span className="view-detail-value">{selectedKey.clientIp || 'No restriction'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Last Used</span>
                    <span className="view-detail-value">{formatDate(selectedKey.lastUsedAt)}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Usage Count</span>
                    <span className="view-detail-value">{selectedKey.usageCount || 0}</span>
                  </div>
                  <div className="view-detail-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="view-detail-label">Description</span>
                    <span className="view-detail-value">{selectedKey.description || 'No description'}</span>
                  </div>
                </div>
              </div>

              <div className="view-details-section">
                <h4 className="view-details-title"><i className="fas fa-tachometer-alt"></i> Rate Limits</h4>
                <div className="view-details-grid">
                  <div className="view-detail-item">
                    <span className="view-detail-label">Trades</span>
                    <span className="view-detail-value">
                      {selectedKey.rateLimitOverride?.trades || '60'} / min
                    </span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Stats</span>
                    <span className="view-detail-value">
                      {selectedKey.rateLimitOverride?.stats || '120'} / min
                    </span>
                  </div>
                </div>
              </div>

              <div className="view-details-section">
                <h4 className="view-details-title"><i className="fas fa-shield-alt"></i> Permissions</h4>
                <div className="permission-display">
                  <div className={`permission-item ${selectedKey.permissions?.canAccessTrades ? 'granted' : 'denied'}`}>
                    <i className={`fas ${selectedKey.permissions?.canAccessTrades ? 'fa-check' : 'fa-times'}`}></i>
                    <span>Can Access Trades</span>
                  </div>
                  <div className={`permission-item ${selectedKey.permissions?.canAccessStats ? 'granted' : 'denied'}`}>
                    <i className={`fas ${selectedKey.permissions?.canAccessStats ? 'fa-check' : 'fa-times'}`}></i>
                    <span>Can Access Stats</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowViewModal(false); setSelectedKey(null); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyManager;